// SimulationShell — dispatches loadDefaultExample on mount.
//
// The scenarioSaga handles all orchestration: fetching manifest, topology,
// scenario, dispatching to stores. This component just triggers the intent
// and renders based on loading/error/ready state.

import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectSimulationStatus } from '../../store/slices/simulationSlice.ts';
import {
  selectSourceLoading,
  selectSourceError,
  selectManifest,
} from '../../store/slices/scenarioSourceSlice.ts';
import { loadDefaultExample } from '../../store/sagas/scenarioSaga.ts';

export interface SimulationShellProps {
  children?: React.ReactNode;
}

export default function SimulationShell({ children }: SimulationShellProps) {
  const dispatch = useDispatch();
  const simulationStatus = useSelector(selectSimulationStatus);
  const loading = useSelector(selectSourceLoading);
  const error = useSelector(selectSourceError);
  const manifest = useSelector(selectManifest);

  const dispatched = useRef(false);

  useEffect(() => {
    if (dispatched.current) return;
    dispatched.current = true;
    dispatch(loadDefaultExample());
  }, [dispatch]);

  if (error) {
    return (
      <div className="simulation-error" role="alert">
        <h2>Simulation Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  // Still loading: saga is actively fetching the initial example data.
  // Once the example manifest is loaded, always render children (even if
  // user switches to "My Project" tab which may have no manifest yet).
  if (loading && !manifest) {
    return (
      <div className="simulation-loading" role="status">
        <p>Loading simulation…</p>
      </div>
    );
  }

  return (
    <div className="simulation-shell" data-status={simulationStatus}>
      {children}
    </div>
  );
}
